import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, take } from 'rxjs';
import { WidgetData } from 'src/app/api/bizzmine/dashboard/dashboard-api.service';
import { DocumentTreeApiService } from 'src/app/api/bizzmine/document-tree/document-tree-api.service';
import { FilterApiService } from 'src/app/api/bizzmine/filter/filter-api.service';
import { SidebarApiService } from 'src/app/api/bizzmine/sidebar/sidebar-api.service';
import { WidgetApiService } from 'src/app/api/bizzmine/widget/widget-api.service';
import { DocumentTreeWidgetData } from 'src/models/ts/document-tree-widget-data.model';
import { SeparatorListDto } from 'src/models/ts/separator-list-dto.model';
import { TableViewWidgetData } from 'src/models/ts/table-view-widget-data.model';
import { WidgetsType } from 'src/models/ts/widgets-type.model';
import { WorkspaceSidebarItemDto } from 'src/models/ts/workspace-sidebar-item-dto.model';
import { KeepOfflineService } from '../keep-offline/keep-offline.service';
import { PwaService } from '../pwa/pwa.service';
import { SyncService } from '../sync/sync.service';
import { DocumentTreeNode } from 'src/app/features/bizzmine/widgets/document-tree-widget/document-tree-widget.component';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceOfflineService {

  private readonly sidebarApiService = inject(SidebarApiService);
  private readonly http = inject(HttpClient);
  private readonly widgetApiService = inject(WidgetApiService);
  private readonly filterApiService = inject(FilterApiService);
  private readonly documentTreeApiService = inject(DocumentTreeApiService);
  private readonly keepOfflineService = inject(KeepOfflineService);
  private readonly pwaService = inject(PwaService);
  private readonly syncService = inject(SyncService)
  
  public fetchOfflineWorkspace(): void {
    this.sidebarApiService.getOfflineSidebar().pipe(take(1)).subscribe({
      next: (data) => {
        if (data.Offline) {
          this.syncService.sync();
          this.pwaService.getMaxDaysOfflinePwaUsage();
          forkJoin([
            this.http.get('api/sidebar/offline', { observe: 'response' }),
            this.http.get('api/usersettings', { observe: 'response' }),
            this.http.get('api/usersettingsindex', { observe: 'response' }),
          ]).subscribe({
            next: (results) => {
              results.forEach((result) => {
                this.keepOfflineService.saveDataInIndexedDb(result.url!, result.body);
              });
            }});

          this.saveWorkspaceInCache(data.ID);
        }
      },
      error: (error) => {
        console.error('Error downloading offline workspace sidebar:', error);
      }
    });
  }

  
  private saveWidgetDataInCache(widgetData: WidgetData): void {
    switch (widgetData.WidgetsType) {
      case WidgetsType.DocumentTree:
        this.saveDocumentTreeFolderInCache(widgetData as DocumentTreeWidgetData);
        break;
      case WidgetsType.ListOfCollectionItems:
        this.saveListDataInCache(widgetData as TableViewWidgetData);
        break;
      case WidgetsType.All:
      case WidgetsType.CollectionInfoWidget:
      case WidgetsType.HTMLWidget:
      case WidgetsType.RSSFeed:
      case WidgetsType.Chart:
      case WidgetsType.Scheduler:
      case WidgetsType.TrainingQueue:
      case WidgetsType.TrainingSkillMatrixUser:
      case WidgetsType.TrainingSkillMatrixFunction:
      case WidgetsType.TrainingGapAnalysisMatrix:
      case WidgetsType.TaskListWidget:
      case WidgetsType.CopilotWidget:
      case WidgetsType.QualificationMatrixUser:
      case WidgetsType.QualificationMatrixSkill:
        throw new Error('WidgetType not yet implemented');
    }
  }

  
  private saveWorkspaceParentInCache(parent: SeparatorListDto): void {
    parent.Children.push(parent.Parent);
    parent.Children.forEach((child: WorkspaceSidebarItemDto) => {
      this.widgetApiService.getWidgetByWorkspaceItemIdResponse(child.WorkspaceItemsID).subscribe(response => {
        if (response.body != null && response.url != null) {
          this.keepOfflineService.saveDataInIndexedDb(response.url, response.body);
          this.saveWidgetDataInCache(response.body);
        }
      });
    });
  }

  private saveWorkspaceInCache(id: number): void {
    this.sidebarApiService.getWorkspaceSideBarResponse(id).subscribe(response => {

      if (response.body != null && response.url != null) {
        this.keepOfflineService.saveDataInIndexedDb(response.url, response.body);
        response.body.Parents.forEach(parent => {
          this.pwaService.addHardcodedRoute('/workspace/' + id );
          this.pwaService.addHardcodedRoute('/workspace/' + id + '/widget/' + parent.Parent.WorkspaceItemsID);
          this.saveWorkspaceParentInCache(parent);
        });
        this.pwaService.addItemsToCache();
      }
    });
  }

  
  private saveListDataInCache(widgetData: TableViewWidgetData): void {
    this.keepOfflineService.saveListSearchObservable(widgetData.ListOptions.ListID, widgetData.ID, widgetData.ListOptions.CanViewHiddenDocuments).subscribe();
    widgetData.FilterList.forEach(
      (filter: any) => {
        this.filterApiService.getFilterDataReponse(filter.ID).subscribe({
          next: (
            filterRes
          ) => {
            this.keepOfflineService.saveDataInIndexedDb(filterRes.url, filterRes.body);
          }
        });
      }
    );
  }
  
  private saveDocumentTreeFolderInCache(widgetData: DocumentTreeWidgetData): void {
    this.documentTreeApiService.getDocumentTreeDataReponse(widgetData.ID, 0, { CanViewHiddenDocuments: widgetData.CanViewHiddenDocuments })
      .subscribe({
        next: (list) => {
          if (list.url != null && list.body != null) {
            // Save the full tree
           let documentTreeNode = list.body[0] as DocumentTreeNode;
            this.keepOfflineService.saveDataInIndexedDb(list.url, list.body);
            this.documentTreeApiService.getDocumentTreeDataReponse(widgetData.ID, list.body[0].id, { CanViewHiddenDocuments: widgetData.CanViewHiddenDocuments }
            ).subscribe({
                next: (node) => {
                  if (node.url != null && node.body != null) {
                    documentTreeNode.children = node.body as DocumentTreeNode[]; 
                    this.syncService.syncDocumentTreeFolderStructure(documentTreeNode, widgetData);
                    this.keepOfflineService.saveDataInIndexedDb(node.url, node.body);
                  }
                }
              }
            );
          }
        },
        error: (error) => {
          console.error(
            'Error downloading dashboard data:',
            error
          );
        }
      });
  }
}
